import { ReadonlyVec2, ReadonlyVec3, vec2 } from 'gl-matrix';
import { DoseMatrix } from '../../../domain/patient/doseMatrix';
import { EnvironmentObject } from '../common/EnvironmentObject';
import { Grid } from '../common/Grid';
import { DoseTexture } from './DoseTexture';

export const DEFAULT_DOSE_VISIBLE = true;
export const DEFAULT_DOSE_OPACITY_VALUE = 0.5;
export const DEFAULT_MIN_DOSE_RANGE = 0.25;
export const DEFAULT_MAX_DOSE_RANGE = 1;

/**
 * {@link DoseDistribution} is a data model for dose.\
 * It inherits from {@link EnvironmentObject} as it has physical properties and may be visualized.
 */
export class DoseDistribution extends EnvironmentObject {
    /**
     * {@link DoseTexture}, which stores the actual physical properties and {@link WebGLTexture texture} data of the {@link DoseDistribution}.
     */
    private texture: DoseTexture;

    /**
     * The parent DoseMatrix object.
     */
    private readonly _parent: DoseMatrix;
    public get parent(): DoseMatrix {
        return this._parent;
    }

    public get sopInstanceUID(): string {
        return this.parent.dicomUid;
    }

    /**
     * The selected dose range.
     */
    private _selectedDoseRange: ReadonlyVec2 = vec2.fromValues(
        DEFAULT_MIN_DOSE_RANGE,
        DEFAULT_MAX_DOSE_RANGE
    );
    public get selectedDoseRange(): ReadonlyVec2 {
        return this._selectedDoseRange;
    }

    public set selectedDoseRange(range: ReadonlyVec2) {
        this._selectedDoseRange = range;
    }

    /** The visibility of the dose distribution. */
    private _isVisible: boolean = DEFAULT_DOSE_VISIBLE;
    public get isVisible(): boolean {
        return this._isVisible;
    }

    public set isVisible(visible: boolean) {
        this._isVisible = visible;
    }

    private _alphaLevelOpacity: number = DEFAULT_DOSE_OPACITY_VALUE;

    public get alphaLevelOpacity(): number {
        return this._alphaLevelOpacity;
    }

    public set alphaLevelOpacity(opacity: number) {
        this._alphaLevelOpacity = opacity;
    }

    /**
     * Constructor that will create a {@link DoseDistribution} from {@link Dose}.
     * @param dose The {@link Dose} datamodel that contains all the data needed for the creation of {@link DoseDistribution}.
     */
    public constructor(dose: DoseMatrix) {
        super();
        this.texture = new DoseTexture(dose);
        this._parent = dose;
    }

    public get grid(): Grid {
        return this.parent.grid;
    }

    /**
     * Method to get the range of color values in the {@link DoseTexture}.
     */
    public get range(): ReadonlyVec2 {
        return this.texture.range;
    }

    /**
     * Method to bind the {@link texture DoseTexture}'s {@link DoseTexture.texture texture}.
     */
    public bindTexture(): void {
        this.texture.bindTexture();
    }

    public getDoseIntensityValueAtCursorCoordinate(
        patientCoords: ReadonlyVec3
    ): number | null {
        const cursorVoxelCoords = this.parent.grid.toVoxelSpace(patientCoords);
        const doseIntensityValueAtCoordinate =
            this.texture.getDoseIntensityValueAtCursorCoordinate(
                cursorVoxelCoords
            );
        return doseIntensityValueAtCoordinate;
    }
    /**
     * Method to delete the {@link texture DoseTexture}.
     */
    public delete(): void {
        this.texture.delete();
    }
}
